// Render mode for the Round Up Report PNG export.
//
// The export pipeline (GitHub Action → Playwright) injects `window.__RENDER_DATA__`
// — the dataset returned by the `roundup-export-data` Edge Function — BEFORE any app
// code runs. When that's present, the app renders the Round Up Report entirely from
// the injected bundle: ZERO live API calls, no login, no credentials in the page.
//
// This module exposes the flag and a Supabase-client shim that serves every query the
// page makes from the bundle. The Cortex path is handled in theBarn/utils#cortexQuery.

export const RENDER_DATA =
  typeof window !== 'undefined' && window.__RENDER_DATA__ ? window.__RENDER_DATA__ : null;
export const isRenderMode = !!RENDER_DATA;

// A minimal, chainable stand-in for the supabase-js query builder. Every filter/
// modifier is a no-op that returns the builder; awaiting it (or .single/.maybeSingle)
// resolves to { data, count, error } from the provided resolver.
function builder(resolve) {
  const state = { eqs: {}, ins: {}, range: null };
  const exec = () => {
    const out = resolve(state) || {};
    let data = out.data ?? [];
    // Honor .range() the way PostgREST does. A no-op range replayed the whole
    // bundle on every page request; when the bundle was EXACTLY rpcAll's page
    // size (the capped 2026-08-13 export), the short-page stop rule never
    // fired and every tile rendered 30×. count stays the full-set size so
    // callers see PostgREST's content-range semantics.
    const count = out.count ?? (Array.isArray(data) ? data.length : null);
    if (state.range && Array.isArray(data)) data = data.slice(state.range[0], state.range[1] + 1);
    return Promise.resolve({ data, count, error: null, status: 200, statusText: 'OK' });
  };
  const b = {
    select() { return b; },
    order() { return b; },
    range(from, to) { state.range = [from, to]; return b; },
    limit() { return b; },
    gte() { return b; }, lte() { return b; }, gt() { return b; }, lt() { return b; },
    neq() { return b; }, ilike() { return b; }, like() { return b; },
    or() { return b; }, not() { return b; }, filter() { return b; }, contains() { return b; },
    eq(col, val) { state.eqs[col] = val; return b; },
    in(col, vals) { state.ins[col] = vals; return b; },
    single() { return exec().then((r) => ({ ...r, data: Array.isArray(r.data) ? (r.data[0] ?? null) : r.data })); },
    maybeSingle() { return exec().then((r) => ({ ...r, data: Array.isArray(r.data) ? (r.data[0] ?? null) : r.data })); },
    then(onF, onR) { return exec().then(onF, onR); },
    catch(onR) { return exec().catch(onR); },
    finally(fn) { return exec().finally(fn); },
  };
  return b;
}

export function makeRenderClient(RD) {
  const warn = (what) => console.warn('[render-mode] un-injected call →', what, '(returned empty — bundle may be missing data)');
  return {
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      getUser: async () => ({ data: { user: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe() {} } } }),
      signOut: async () => ({ error: null }),
    },
    from(table) {
      return builder((state) => {
        switch (table) {
          case 'vendor_aliases': return { data: RD.vendorAliases || [] };
          case 'range_reports': return { data: RD.rangeReports || [] };
          case 'range_report_tag_options': return { data: RD.tagOptions || [] };
          case 'trail_notes': return { data: RD.trailNotes || [] };
          case 'wfx_items': {
            const div = state.eqs['item_division->>division_name'];
            if (div) return { data: (RD.wfxByDivision && RD.wfxByDivision[div]) || [] };
            return { data: [] }; // ProductImage npdIndex image-fallback — best effort
          }
          case 'po_shipment_summary': return { data: [] }; // CC detail modal only (not screenshotted)
          default: warn(`from('${table}')`); return { data: [] };
        }
      });
    },
    rpc(fn) {
      return builder(() => {
        switch (fn) {
          case 'get_launch_lineup_data': return { data: RD.ccData || [] };
          case 'get_po_data': return { data: [], count: 0 }; // feeds only dead roundUpMetrics
          case 'get_filter_options': return { data: RD.filterOptions || [] };
          case 'get_po_dashboard_stats': return { data: [] };
          case 'get_user_permissions': return { data: [] };
          default: warn(`rpc('${fn}')`); return { data: [] };
        }
      });
    },
    functions: {
      invoke: async (name) => { warn(`functions.invoke('${name}')`); return { data: null, error: null }; },
    },
    channel() { const ch = { on() { return ch; }, subscribe() { return ch; } }; return ch; },
    removeChannel() {},
  };
}

// Returns the GraphQL `data` object for a Cortex query from the injected bundle.
// We collapse pagination: every colorways page is merged server-side, so we return
// all nodes in one page (hasNextPage:false) and the frontend's paging loop runs once.
export function renderModeCortex(graphql) {
  const cx = (RENDER_DATA && RENDER_DATA.cortex) || {};
  const g = String(graphql).replace(/\s+/g, ' ');
  if (g.includes('inventory(')) return cx.inventory || {};
  if (g.includes('product {')) return { colorways: { nodes: cx.attrColorways || [], pageInfo: { hasNextPage: false } } };
  if (g.includes('image { url }')) return { colorways: { nodes: cx.imageColorways || [], pageInfo: { hasNextPage: false } } };
  return {};
}
